import type { OnboardingStepContext } from "@/pages/onboarding/stepMachine/onboardingStepContext";

/**
 * The semantic onboarding steps, one name per real state across all three path
 * families. This de-overloads the `viewStep` integer (where e.g. 2 = situation
 * OR plan-gate, 6 = cert-upload OR personalize OR cert). `stepToViewStep`
 * re-projects to the public integer, which stays the wizard's wire value.
 */
export type Step =
  // shared entry
  | "intro"
  | "who_setup"
  // pre-DGI plan-selection gates (self + accountant-flow owner)
  | "regime"
  // shared by self + accountant-user
  | "situation"
  | "dgi_access"
  // self / non-accountant
  | "cert_choice"
  | "cert_content"
  | "abitab"
  | "cae"
  | "cfe"
  | "fiscal"
  | "literal_e"
  | "personalize"
  | "plan"
  | "plan_migrating"
  | "done"
  // accountant-flow owner
  | "plan_gate"
  | "acc_owner_invite"
  | "acc_owner_steps"
  | "acc_owner_personalize"
  | "acc_owner_plan"
  | "acc_owner_success"
  // accountant user
  | "acc_user_company"
  | "acc_user_literal_e"
  | "acc_user_cert"
  | "acc_user_success";

type ProjectionCtx = Pick<
  OnboardingStepContext,
  "ownerPostCertViewSteps" | "totalSteps" | "preCertOffset"
>;

/**
 * Project a semantic Step to the public `viewStep` integer. Path-dependent for
 * the post-cert tail (via `ownerPostCertViewSteps`) and `done` (`totalSteps`),
 * fixed for the accountant families.
 *
 * The self / non-accountant path inserts two pre-DGI plan-selection steps
 * (`regime` = 2, `plan_gate` = 3), so `situation` and everything after it shift
 * by `preCertOffset` (2 for self, 0 otherwise). `regime` / `plan_gate` sit at the
 * fixed 2 / 3 for both plan-first families (accountant-flow owner uses offset 0
 * and has no situation/dgi, so nothing after them shifts for it). The
 * accountant-only cases keep their fixed integers.
 */
export function stepToViewStep(step: Step, ctx: ProjectionCtx): number {
  const st = ctx.ownerPostCertViewSteps;
  const off = ctx.preCertOffset;
  switch (step) {
    case "intro":
      return 0;
    case "who_setup":
      return 1;
    case "regime":
      return 2;
    case "plan_gate":
      return 3;
    case "situation":
      return 2 + off;
    case "dgi_access":
      return 3 + off;
    case "cert_choice":
      return 4 + off;
    case "acc_owner_invite":
    case "acc_user_company":
      return 4;
    case "cert_content":
      return 5 + off;
    case "acc_owner_steps":
      return 5;
    case "abitab":
      return 6 + off;
    case "acc_owner_personalize":
    case "acc_user_literal_e":
      return 6;
    case "acc_owner_plan":
    case "acc_user_cert":
      return 7;
    case "acc_owner_success":
    case "acc_user_success":
      return 8;
    case "cae":
      return st.cae;
    case "cfe":
      // Only reached when needsCfeImportStep allocated the slot.
      return st.cfe ?? st.cae;
    case "fiscal":
      return st.fiscal;
    case "literal_e":
      return st.literalE ?? st.personalize;
    case "personalize":
      return st.personalize;
    case "plan":
      return st.migrationOrPlanFirst;
    case "plan_migrating":
      return st.planMigrating;
    case "done":
      return ctx.totalSteps;
  }
}
